import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  categoryTree as defaultTree,
  products as defaultProducts,
} from '../mock';

const API = process.env.REACT_APP_BACKEND_URL;

// Adapter: convert flat API response into the nested shape the UI expects
// API shape: {categories:[{slug,title,subtitle,description,image,order}],
//             subcategories:[{parent_slug,slug,title,short,order}],
//             products:[{product_id, sku, name, category, subcategory, ...}]}
// UI shape: categoryTree: [{slug, title, subtitle, description, image,
//                           subcategories:[{slug,title,short}]}]
//           products:     [{id, sku, name, category, subcategory, ..., image, specs, features}]
function reshape(data) {
  const catCount = {};
  const subCount = {};
  (data.products || []).forEach((p) => {
    catCount[p.category] = (catCount[p.category] || 0) + 1;
    if (p.subcategory) subCount[p.subcategory] = (subCount[p.subcategory] || 0) + 1;
  });
  const subsByParent = {};
  (data.subcategories || []).forEach((s) => {
    (subsByParent[s.parent_slug] ||= []).push({
      slug: s.slug,
      title: s.title,
      short: s.short,
      count: subCount[s.slug] || 0,
      alwaysVisible: !!s.always_visible,
    });
  });
  const categoryTree = (data.categories || []).map((c) => ({
    slug: c.slug,
    title: c.title,
    subtitle: c.subtitle,
    description: c.description,
    image: c.image,
    count: catCount[c.slug] || 0,
    alwaysVisible: !!c.always_visible,
    subcategories: subsByParent[c.slug] || [],
  }));
  const products = (data.products || []).map((p) => ({
    ...p,
    id: p.product_id || p.id,
  }));
  return { categoryTree, products };
}

// Session-level cache: catalog is fetched once and shared by all consumers
// (Navbar, Home, Products) so SPA navigation never re-fetches.
let cache = null;
let inflight = null;

function fetchCatalog() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = axios
      .get(`${API}/api/site/catalog`)
      .then((r) => {
        if (r.data?.categories?.length) cache = reshape(r.data);
        return cache;
      })
      .catch(() => cache);
  }
  return inflight;
}

export function useCatalog() {
  const [categoryTree, setTree] = useState(cache?.categoryTree || defaultTree);
  const [products, setProducts] = useState(cache?.products || defaultProducts);

  useEffect(() => {
    let alive = true;
    fetchCatalog().then((data) => {
      if (alive && data) {
        setTree(data.categoryTree);
        setProducts(data.products);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const findSubcategory = (parentSlug, subSlug) => {
    const parent = categoryTree.find((c) => c.slug === parentSlug);
    return parent?.subcategories.find((s) => s.slug === subSlug);
  };

  return { categoryTree, products, findSubcategory };
}
